/**
 * useExpectedRevenue — fetch the schedule-spine expected-revenue rollup for the
 * active entity (paid-for-work company home). Thin wrapper over
 * api.getExpectedRevenue(); refetches on mount + activeEntity change. The
 * endpoint is per-active-entity via the X-Entity-Id header (auto-injected).
 *
 * Returns null for callers in local mode or with no active entity.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, isInLocalMode, type ExpectedRevenueSummary } from '@/lib/api';
import { useAppStore } from '@/stores/useAppStore';

export function useExpectedRevenue() {
  const { activeEntity } = useAppStore();
  const [summary, setSummary] = useState<ExpectedRevenueSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef(false);

  const refresh = useCallback(async () => {
    if (isInLocalMode() || !activeEntity?.id) {
      setSummary(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await api.getExpectedRevenue();
      if (!cancelRef.current) setSummary(r);
    } catch (e) {
      if (!cancelRef.current) {
        setError(e instanceof Error ? e.message : 'Could not load expected revenue.');
        setSummary(null);
      }
    } finally {
      if (!cancelRef.current) setLoading(false);
    }
  }, [activeEntity?.id]);

  useEffect(() => {
    cancelRef.current = false;
    refresh();
    return () => {
      cancelRef.current = true;
    };
  }, [refresh]);

  return { summary, loading, error, refresh };
}
